import { useState, useEffect } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { Mail, ArrowRight, AlertCircle, Check, ArrowLeft } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { t } from '../data/translations';
import { authApi } from '../api/auth';

export default function ForgotPasswordPage() {
  usePageTitle("Réinitialisation du mot de passe — Lodiss");

  const { lang, setPage } = useApp();
  const T = t[lang];

  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!email.trim() || !email.includes('@')) {
      setError(lang === 'fr' ? 'Email invalide' : 'Invalid email');
      return;
    }

    try {
      setLoading(true);
      await authApi.forgotPassword(email);
      setSuccess(true);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <main className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-xl border border-gray-100 p-8 shadow-sm">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-emerald-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-950">
                {lang === 'fr' ? 'Email envoyé' : 'Email sent'}
              </h1>
              <p className="text-sm text-gray-500 mt-2">
                {lang === 'fr' 
                  ? 'Si un compte existe avec cette adresse email, vous recevrez un lien de réinitialisation.'
                  : 'If an account exists with this email address, you will receive a reset link.'}
              </p>
            </div>

            <button
              onClick={() => setPage('login')}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-lodiss-red hover:bg-lodiss-dark text-white font-semibold rounded-lg transition-colors duration-150"
            >
              <ArrowLeft size={16} />
              {lang === 'fr' ? 'Retour à la connexion' : 'Back to login'}
            </button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl border border-gray-100 p-8 shadow-sm">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-950">
              {lang === 'fr' ? 'Mot de passe oublié' : 'Forgot password'}
            </h1>
            <p className="text-sm text-gray-500 mt-2">
              {lang === 'fr' 
                ? 'Entrez votre email pour recevoir un lien de réinitialisation'
                : 'Enter your email to receive a reset link'}
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-lg flex items-start gap-3">
              <AlertCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                Email
              </label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-lodiss-red focus:ring-lodiss-red/20"
                  placeholder="vous@entreprise.com"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-lodiss-red hover:bg-lodiss-dark text-white font-semibold rounded-lg transition-colors duration-150 disabled:opacity-50"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  {lang === 'fr' ? 'Envoyer le lien' : 'Send reset link'}
                  <ArrowRight size={16} />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setPage('login')}
              className="text-sm text-gray-500 hover:text-lodiss-red font-medium flex items-center justify-center gap-1 mx-auto"
            >
              <ArrowLeft size={14} />
              {lang === 'fr' ? 'Retour à la connexion' : 'Back to login'}
            </button>
          </div>
        </div>
      </div>
    </main>
  );
}
